import {
  getClass,
  getCantripsForClass,
  getSpellsForClassAtLevel,
} from '@character-creator/data';

/**
 * Curated spell selections for character templates (level 1)
 * These are thematic, well-rounded choices for each spellcasting class
 */
export const CURATED_TEMPLATE_SPELLS: Record<
  string,
  { cantrips: string[]; spells: string[] }
> = {
  wizard: {
    // 3 cantrips at level 1
    cantrips: ['fire-bolt', 'prestidigitation', 'mage-hand'],
    // 6 spells in spellbook at level 1
    spells: [
      'magic-missile',
      'shield',
      'mage-armor',
      'detect-magic',
      'find-familiar',
      'sleep',
    ],
  },
  cleric: {
    // 3 cantrips at level 1
    cantrips: ['sacred-flame', 'guidance', 'spare-the-dying'],
    // Prepared caster - these are good defaults to have prepared
    spells: ['cure-wounds', 'healing-word', 'bless', 'shield-of-faith'],
  },
  bard: {
    // 2 cantrips at level 1
    cantrips: ['vicious-mockery', 'minor-illusion'],
    // 4 spells known at level 1
    spells: ['healing-word', 'dissonant-whispers', 'faerie-fire', 'thunderwave'],
  },
  druid: {
    // 2 cantrips at level 1
    cantrips: ['druidcraft', 'produce-flame'],
    // Prepared caster - good defaults
    spells: ['entangle', 'cure-wounds', 'healing-word', 'thunderwave'],
  },
  sorcerer: {
    // 4 cantrips at level 1
    cantrips: ['fire-bolt', 'minor-illusion', 'prestidigitation', 'ray-of-frost'],
    // 2 spells known at level 1
    spells: ['magic-missile', 'shield'],
  },
  warlock: {
    // 2 cantrips at level 1
    cantrips: ['eldritch-blast', 'minor-illusion'],
    // 2 spells known at level 1
    spells: ['hex', 'armor-of-agathys'],
  },
  paladin: {
    // No cantrips, spellcasting starts at level 2
    cantrips: [],
    // Prepared caster - these would be good when they reach level 2
    spells: [],
  },
  ranger: {
    // No cantrips, spellcasting starts at level 2
    cantrips: [],
    // Spells known - these would be good when they reach level 2
    spells: [],
  },
};

/**
 * Thematic equipment selections for each class
 * Maps class ID to array of preferred option indices for each equipment choice
 */
export const THEMATIC_EQUIPMENT_SELECTIONS: Record<string, Record<number, number>> = {
  // Barbarian: greataxe, 2 handaxes (+ explorer pack, 4 javelins are fixed)
  barbarian: {
    0: 0, // greataxe (vs any martial melee)
    1: 0, // 2 handaxes (vs any simple)
  },
  // Cleric: warhammer (if proficient), chain mail (Life domain), crossbow, priest pack
  cleric: {
    0: 1, // warhammer (vs mace) - dwarf gets proficiency
    1: 2, // chain-mail (vs scale-mail, leather) - Life domain gives heavy armor
    2: 0, // light crossbow + bolts (vs any simple)
    3: 0, // priest pack (vs explorer pack)
  },
  // Fighter: chain mail, martial + shield, handaxes, dungeoneer pack
  fighter: {
    0: 0, // chain-mail (vs leather + longbow)
    1: 0, // martial weapon + shield (vs two martial weapons)
    2: 1, // 2 handaxes (vs light crossbow)
    3: 0, // dungeoneer pack (vs explorer pack)
  },
  // Rogue: rapier, shortbow + arrows, burglar pack (+ leather, 2 daggers, thieves tools fixed)
  rogue: {
    0: 0, // rapier (vs shortsword)
    1: 0, // shortbow + quiver + arrows (vs shortsword)
    2: 0, // burglar pack (vs dungeoneer, explorer)
  },
  // Wizard: quarterstaff, arcane focus, scholar pack (+ spellbook fixed)
  wizard: {
    0: 0, // quarterstaff (vs dagger)
    1: 1, // arcane focus (vs component pouch)
    2: 0, // scholar pack (vs explorer pack)
  },
  // Bard: rapier, diplomat pack, lute (+ leather armor, dagger fixed)
  bard: {
    0: 0, // rapier (vs longsword, any simple)
    1: 0, // diplomat pack (vs entertainer pack)
    2: 0, // lute (vs any musical instrument)
  },
  // Druid: wooden shield, scimitar, leather armor, explorer pack (+ druidic focus fixed)
  druid: {
    0: 0, // wooden shield (vs simple weapon)
    1: 0, // scimitar (vs simple melee)
    2: 0, // leather armor (set, no choice actually)
    3: 0, // explorer pack (set, no choice actually)
  },
  // Monk: shortsword, dungeoneer pack (+ 10 darts fixed)
  monk: {
    0: 0, // shortsword (vs any simple)
    1: 0, // dungeoneer pack (vs explorer pack)
  },
  // Paladin: martial + shield, javelins, priest pack (+ chain mail, holy symbol fixed)
  paladin: {
    0: 0, // martial + shield (vs two martial)
    1: 0, // 5 javelins (vs any simple melee)
    2: 0, // priest pack (vs explorer pack)
  },
  // Ranger: scale mail, shortswords, dungeoneer pack (+ longbow, quiver, arrows fixed)
  ranger: {
    0: 0, // scale mail (vs leather armor)
    1: 0, // two shortswords (vs two simple melee)
    2: 0, // dungeoneer pack (vs explorer pack)
  },
  // Sorcerer: light crossbow, arcane focus, dungeoneer pack (+ 2 daggers fixed)
  sorcerer: {
    0: 0, // light crossbow + bolts (vs any simple)
    1: 1, // arcane focus (vs component pouch)
    2: 0, // dungeoneer pack (vs explorer pack)
  },
  // Warlock: light crossbow, arcane focus, scholar pack (+ leather, any simple, 2 daggers fixed)
  warlock: {
    0: 0, // light crossbow + bolts (vs any simple)
    1: 1, // arcane focus (vs component pouch)
    2: 1, // scholar pack (vs dungeoneer pack)
    3: 0, // any simple weapon - dagger is good
  },
};

/**
 * Preferred weapons when resolving "any-X" tokens
 * Maps class ID to token -> weapon ID
 */
export const ANY_WEAPON_PREFERENCES: Record<string, Record<string, string>> = {
  fighter: {
    'any-martial': 'longsword',
    'any-martial-melee': 'longsword',
    'any-simple': 'handaxe',
  },
  barbarian: {
    'any-martial-melee': 'greatsword',
    'any-simple': 'javelin',
  },
  cleric: {
    'any-simple': 'mace',
  },
  rogue: {
    'any-simple': 'dagger',
  },
  bard: {
    'any-simple': 'dagger',
  },
  paladin: {
    'any-martial': 'longsword',
    'any-simple-melee': 'javelin',
  },
  ranger: {
    'any-simple-melee': 'shortsword',
  },
  warlock: {
    'any-simple': 'dagger',
  },
  sorcerer: {
    'any-simple': 'dagger',
  },
  druid: {
    'any-simple': 'quarterstaff',
    'any-simple-melee': 'quarterstaff',
  },
  monk: {
    'any-simple': 'shortsword',
  },
};

/**
 * Get thematic equipment selections for a template
 * Returns Record<number, number> mapping choice index to selected option
 */
export function getThematicEquipmentSelections(classId: string): Record<number, number> {
  return THEMATIC_EQUIPMENT_SELECTIONS[classId] ?? {};
}

/**
 * Get random equipment selections for a class
 * Picks randomly from available options for each choice
 */
export function getRandomEquipmentSelections(classId: string): Record<number, number> {
  const classData = getClass(classId);
  if (!classData) return {};

  const selections: Record<number, number> = {};

  classData.startingEquipment.forEach((item, index) => {
    if (typeof item !== 'string' && 'choose' in item) {
      // It's a choice - randomly select an option
      selections[index] = Math.floor(Math.random() * item.from.length);
    }
  });

  return selections;
}

/**
 * Get curated spells for a template (better than random)
 */
export function getCuratedSpellSelection(classId: string): {
  cantrips: string[];
  knownSpells: string[];
} {
  const curated = CURATED_TEMPLATE_SPELLS[classId];
  if (!curated) {
    return { cantrips: [], knownSpells: [] };
  }
  return {
    cantrips: curated.cantrips,
    knownSpells: curated.spells,
  };
}

/**
 * Get random spells for a class at a given level
 * Respects class spellcasting rules (cantrips known, spells known, etc.)
 */
export function getRandomSpellSelection(
  classId: string,
  level: number = 1
): {
  cantrips: string[];
  knownSpells: string[];
} {
  const classData = getClass(classId);

  // Non-spellcasting class
  if (!classData?.spellcasting) {
    return { cantrips: [], knownSpells: [] };
  }

  const { spellcasting } = classData;
  const levelIndex = level - 1;

  // Get number of cantrips known at this level
  const cantripsCount = spellcasting.cantripsKnown?.[levelIndex] ?? 0;

  // Get available cantrips for this class and randomly select
  const availableCantrips = getCantripsForClass(classId);
  const shuffledCantrips = [...availableCantrips].sort(() => Math.random() - 0.5);
  const selectedCantrips = shuffledCantrips.slice(0, cantripsCount).map((s) => s.id);

  // Determine how many spells to add
  let spellCount = 0;

  if (spellcasting.spellsKnown) {
    // Classes with spells known (Bard, Sorcerer, Ranger, Warlock)
    spellCount = spellcasting.spellsKnown[levelIndex] ?? 0;
  } else if (spellcasting.type === 'full') {
    // Prepared casters (Cleric, Druid, Wizard)
    // Wizard gets 6 spells in spellbook at level 1
    // Others prepare from full list, but we add sensible defaults
    spellCount = classId === 'wizard' ? 6 : 4;
  } else if (spellcasting.type === 'half') {
    // Half-casters like Paladin - they prepare spells
    // At level 1, they don't have spellcasting yet
    spellCount = levelIndex >= 1 ? 4 : 0;
  }

  // Get available spells for this class at the given level and randomly select
  let knownSpells: string[] = [];
  if (spellCount > 0) {
    const availableSpells = getSpellsForClassAtLevel(classId, level);
    const shuffledSpells = [...availableSpells].sort(() => Math.random() - 0.5);
    knownSpells = shuffledSpells.slice(0, spellCount).map((s) => s.id);
  }

  return { cantrips: selectedCantrips, knownSpells };
}

/**
 * Check if a class is a spellcaster at level 1
 */
export function isSpellcasterAtLevel(classId: string, level: number = 1): boolean {
  const classData = getClass(classId);
  if (!classData?.spellcasting) return false;

  // Half-casters don't get spells until level 2
  if (classData.spellcasting.type === 'half' && level < 2) {
    return false;
  }

  return true;
}
